import { useEffect, useRef, useState, useCallback } from 'react';

const useInfiniteSlider = (enabled: boolean = true) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const resizeTimeoutRef = useRef<number | null>(null);

  const setupAnimation = useCallback(() => {
    if (!enabled || !sliderRef.current) return;

    const slider = sliderRef.current;
    const items = slider.children;
    const originalItemCount = items.length;
    
    if (originalItemCount === 0) return;

    // Prüfe die Benutzer-Präferenz für reduzierte Motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Deaktiviere Animation, aber dupliziere trotzdem für einheitliche Darstellung
      const isAlreadyDuplicated = slider.getAttribute('data-duplicated') === 'true';
      if (!isAlreadyDuplicated) {
        const originalContent = slider.innerHTML;
        slider.innerHTML = originalContent + originalContent;
        slider.setAttribute('data-duplicated', 'true');
      }
      return;
    }

    // Prüfe, ob Inhalte bereits dupliziert wurden
    const isAlreadyDuplicated = slider.getAttribute('data-duplicated') === 'true';
    
    if (!isAlreadyDuplicated) {
      // Dupliziere Inhalte für nahtlosen Übergang
      const originalContent = slider.innerHTML;
      slider.innerHTML = originalContent + originalContent;
      slider.setAttribute('data-duplicated', 'true');
    }

    // Berechne die Breite eines Items (verwende nur die ursprüngliche Anzahl)
    const firstItem = items[0] as HTMLElement;
    
    // Warte kurz, damit der DOM vollständig gerendert ist
    const timeoutId = setTimeout(() => {
      const computedStyle = getComputedStyle(firstItem);
      const itemWidth = firstItem.offsetWidth + 
        parseFloat(computedStyle.marginLeft) + 
        parseFloat(computedStyle.marginRight);

      const totalWidth = itemWidth * originalItemCount;
      const animationDuration = Math.max(totalWidth * 0.02, 15); // Mindestens 15s für sehr flüssige Animation

      // Erstelle CSS-Animation
      const styleId = 'infinite-slider-style';
      const existingStyle = document.getElementById(styleId);
      
      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .infinite-slider {
          animation: smoothScroll ${animationDuration}s linear infinite;
          will-change: transform;
        }
        
        .infinite-slider.paused {
          animation-play-state: paused;
        }
        
        @keyframes smoothScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-${totalWidth}px); }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .infinite-slider {
            animation: none !important;
          }
        }
      `;
      document.head.appendChild(style);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [enabled]);

  useEffect(() => {
    const cleanup = setupAnimation();
    const currentSlider = sliderRef.current; // Lokale Kopie für cleanup
    
    return () => {
      if (cleanup) cleanup();
      const existingStyle = document.getElementById('infinite-slider-style');
      if (existingStyle) {
        existingStyle.remove();
      }
      // Reset für nächste Verwendung
      if (currentSlider) {
        currentSlider.removeAttribute('data-duplicated');
      }
    };
  }, [setupAnimation]);

  // Resize Handler mit Debouncing
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      resizeTimeoutRef.current = window.setTimeout(() => {
        setupAnimation();
      }, 250);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [setupAnimation]);

  // Visibility Change Handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Dynamische Klassennamen basierend auf State
  const getSliderClassName = () => {
    const baseClass = 'infinite-slider';
    const pausedClass = (isHovered || !isVisible) ? 'paused' : '';
    return `${baseClass} ${pausedClass}`.trim();
  };

  const sliderProps = {
    ref: sliderRef,
    className: getSliderClassName(),
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    style: {
      display: 'flex',
      willChange: 'transform'
    }
  };

  return {
    sliderProps,
    isHovered,
    isVisible
  };
};

export default useInfiniteSlider;
